import type { CultureItem } from '@/types/content';

export const cultureItems: CultureItem[] = [
  {
    id: 'mono-no-aware',
    title: '从〈源氏物语〉到现代动画',
    subtitle: '日本“物哀”美学的百年流转',
    summary:
      '为什么日本动画里总有那么多欲言又止的告别？从紫式部到新海诚，“物哀”这条线索从未中断，只是换了表达方式。',
    cover:
      'https://readdy.ai/api/search-image?query=Anime%20style%20editorial%20illustration%20of%20falling%20cherry%20blossoms%20over%20a%20quiet%20temple%20courtyard%20with%20a%20stone%20lantern%2C%20soft%20teal%20twilight%20and%20warm%20paper%20lantern%20glow%2C%20petals%20drifting%20across%20water%2C%20painterly%20digital%20art%2C%20poetic%20wide%20composition&width=1200&height=700&seq=culture-mono-aware&orientation=landscape',
    author: '沈知白',
    date: '2026-10-11',
    readTime: '12 分钟',
    tags: ['美学', '文学', '日本文化'],
    quote: '“物哀不是悲伤，而是看见事物终将消逝时，忽然生出的温柔。”',
    content: [
      {
        heading: '被误读的“物哀”',
        paragraphs: [
          '“物哀”（もののあはれ）常被简化成“伤感”，但在本居宣长的原意里，它更接近一种感知力：面对外在事物时，心里自然涌起的那一层情绪。樱花落下的瞬间，人会不由自主地停一下，这一停，就是物哀。',
          '它不预设价值判断。不是“美所以值得惋惜”，也不是“可惜所以美”，而是先看见、先被打动，然后才谈得上意义。这种先于理性的感受，正是后来日本文艺反复回到的起点。',
        ],
      },
      {
        heading: '从文字到画面',
        paragraphs: [
          '《源氏物语》里大量的季节描写并非背景装饰，而是人物心境的延伸。到了近代，这种写法被电影与动画继承：一个空镜、一场雨、一段没有台词的路，承担了原本由文字完成的工作。',
          '《葬送的芙莉莲》是近年的一个清晰例子。它几乎不使用强烈的情感冲突，而是用旅途中的停顿来积累重量。观众在某个瞬间忽然意识到时间已经过去很久，那一刻的酸楚，正是物哀在现代叙事里的位置。',
        ],
      },
      {
        heading: '为什么它依然有效',
        paragraphs: [
          '在节奏越来越快的媒介环境里，“允许停顿”反而成了一种稀缺体验。物哀提供的不是逃避，而是一种更慢的观看方式：承认失去，然后继续走。',
          '这或许解释了为什么这类作品能跨越文化边界。观众未必知道“物哀”这个词，但都懂得在某个镜头里突然安静下来的感觉。',
        ],
      },
    ],
  },
  {
    id: 'cyberpunk-east-asia',
    title: '赛博朋克在东亚',
    subtitle: '霓虹、义体与城市焦虑',
    summary:
      '赛博朋克诞生于西方，却把最浓的视觉乡愁留给了东亚城市。这是一种想象，也是一面镜子。',
    cover:
      'https://readdy.ai/api/search-image?query=Anime%20style%20editorial%20illustration%20of%20a%20rain%20soaked%20East%20Asian%20megacity%20street%20at%20night%2C%20layered%20neon%20signs%20in%20teal%20and%20amber%2C%20reflections%20in%20puddles%2C%20distant%20silhouettes%20with%20umbrellas%2C%20painterly%20digital%20art%2C%20cinematic%20wide%20composition&width=1200&height=700&seq=culture-cyberpunk-city&orientation=landscape',
    author: '林砚',
    date: '2026-10-04',
    readTime: '15 分钟',
    tags: ['赛博朋克', '城市', '视觉文化'],
    quote: '“霓虹不是为了照亮街道，而是为了让街道看起来像未来。”',
    content: [
      {
        heading: '一座被反复书写的城市',
        paragraphs: [
          '从《神经漫游者》到《银翼杀手》，赛博朋克的视觉语汇几乎从一开始就借用了香港、东京、首尔的街景：密集的招牌、潮湿的巷道、层层叠叠的居住空间。西方创作者把这里当成未来的样子，而生活在这里的人看到的却是当下。',
          '这种错位构成了赛博朋克最有意思的部分。它既是想象的异国，也是真实的城市焦虑被翻译成霓虹的过程。',
        ],
      },
      {
        heading: '东亚创作者的回应',
        paragraphs: [
          '当东亚创作者自己开始书写赛博朋克，重点往往从“奇观”转向“生活”。《攻壳机动队》关心的不是霓虹多漂亮，而是义体化之后的法律与自我；《赛博朋克：边缘行者》则把镜头对准了被城市碾过的年轻人。',
          '《时光代理人》提供了另一种路径：它不强调高科技，而是把“时间”当作可被调用的资源，讨论普通人在城市里的遗憾。这类作品让赛博朋克从视觉风格回到社会寓言。',
        ],
      },
      {
        heading: '风格之后还剩什么',
        paragraphs: [
          '当霓虹与义体成为通用素材，赛博朋克的批判性反而更容易被稀释。真正有价值的作品，往往愿意回答一个不太好看的问题：技术进步之后，谁承担代价？',
          '这也许是我们今天重新阅读这类作品的理由。不是为了看未来有多炫，而是为了确认自己站在哪里。',
        ],
      },
    ],
  },
  {
    id: 'storyboard-language',
    title: '分镜是一种语言',
    subtitle: '押井守与今敏的两种时间',
    summary:
      '同样的镜头长度，在两位导演手里会变成完全不同的时间感。分镜不只是画面安排，它是叙事的语法。',
    cover:
      'https://readdy.ai/api/search-image?query=Anime%20style%20editorial%20illustration%20of%20a%20film%20director%20desk%20with%20storyboard%20sheets%2C%20a%20lightbox%20and%20a%20coffee%20cup%2C%20teal%20window%20light%20with%20warm%20lamp%20glow%2C%20frames%20pinned%20on%20a%20wall%2C%20painterly%20digital%20art%2C%20quiet%20studio%20wide%20composition&width=1200&height=700&seq=culture-storyboard-desk&orientation=landscape',
    author: '周梧',
    date: '2026-09-27',
    readTime: '18 分钟',
    tags: ['分镜', '导演研究', '影像'],
    quote: '“剪辑不是把镜头接起来，而是决定观众什么时候呼吸。”',
    content: [
      {
        heading: '押井守：让画面停下来',
        paragraphs: [
          '押井守的镜头经常“过长”：一个街景、一只鸟、一片水面，停留的时间超出叙事需要。这种处理让观众从故事里被短暂地推出去，被迫观看环境本身。',
          '在《攻壳机动队》里，这种停顿承担了哲学讨论的功能。城市先于人物存在，人物只是穿行其间的意识。观众的不适感，正是作品要传达的疏离。',
        ],
      },
      {
        heading: '今敏：让时间折叠',
        paragraphs: [
          '今敏走的是相反的方向。他用匹配剪辑把不同时空缝合在一起，让现实、梦境与媒介影像互相穿透。《千年女优》里的一次奔跑可以跨越一生，靠的不是旁白，而是镜头的连续性。',
          '这种剪辑要求观众保持高度注意。每一次转场都是一次叙述上的赌注，成功了会产生强烈的眩晕快感，失败了则显得炫技。',
        ],
      },
      {
        heading: '两种时间，同一种诚实',
        paragraphs: [
          '一个是减速，一个是折叠。表面相反，本质都是对“观看”这件事的尊重：不把观众当成被动的接收器，而是要求他们参与时间的建构。',
          '当我们讨论一部动画的“高级感”，往往说的就是这种对时间的掌控力。它不来自预算，而来自导演是否清楚自己想让人看见什么。',
        ],
      },
    ],
  },
  {
    id: 'seiyuu-history',
    title: '声优文化简史',
    subtitle: '从幕后到舞台中央',
    summary:
      '半个世纪前，配音演员的名字不会出现在片尾。今天，他们拥有演唱会、广播与自己的粉丝文化。',
    cover:
      'https://readdy.ai/api/search-image?query=Anime%20style%20editorial%20illustration%20of%20a%20vintage%20radio%20studio%20with%20a%20microphone%20and%20headphones%20on%20a%20wooden%20desk%2C%20warm%20amber%20lighting%20against%20teal%20walls%2C%20floating%20sound%20waves%2C%20painterly%20digital%20art%2C%20nostalgic%20wide%20composition&width=1200&height=700&seq=culture-seiyuu-radio&orientation=landscape',
    author: '顾遥',
    date: '2026-09-19',
    readTime: '10 分钟',
    tags: ['声优', '行业史', '粉丝文化'],
    quote: '“声音一旦被认出来，角色就再也无法被替换。”',
    content: [
      {
        heading: '匿名的年代',
        paragraphs: [
          '早期的动画配音被视为“技术工种”，演员名字很少出现在宣传物料上。观众记住的是角色的声音，而不是声音背后的人。',
          '转折发生在广播与唱片工业的介入。声优开始主持节目、发行角色歌，声音第一次具备了独立的商业价值。',
        ],
      },
      {
        heading: '偶像化的双刃剑',
        paragraphs: [
          '九十年代之后，声优逐渐被纳入偶像体系：写真、演唱会、见面会成为常规配置。这带来了更高的收入与曝光，也让从业者承受了额外审视。',
          '近年行业内对“声优也是表演者”的讨论越来越多。如何在商业期待与专业训练之间取得平衡，成为新的课题。',
        ],
      },
      {
        heading: '声音的重量',
        paragraphs: [
          '无论产业如何变化，声优最核心的工作仍然是让一个不存在的人变得可信。一句呼吸的节奏、一次迟疑，都会改变观众对角色的理解。',
          '当我们为一句话落泪，其实是在回应某种被准确表达的情感。这份准确，值得被看见。',
        ],
      },
    ],
  },
  {
    id: 'doujinshi-ecology',
    title: '同人志的生态',
    subtitle: '创作、流通与共同体',
    summary:
      '在世界最大的同人展上，一张桌子就是一个完整的创作单元。这套自发的流通系统，撑起了二次元文化最活跃的部分。',
    cover:
      'https://readdy.ai/api/search-image?query=Anime%20style%20editorial%20illustration%20of%20a%20small%20indie%20book%20fair%20table%20stacked%20with%20handmade%20zines%20and%20prints%2C%20visitors%20browsing%2C%20warm%20string%20lights%20with%20teal%20shadows%2C%20cozy%20creative%20atmosphere%2C%20painterly%20digital%20art%2C%20wide%20composition&width=1200&height=700&seq=culture-doujinshi-fair&orientation=landscape',
    author: '苏叶',
    date: '2026-09-10',
    readTime: '14 分钟',
    tags: ['同人', '创作生态', '社群'],
    quote: '“一张桌子，就是一个人的出版社。”',
    content: [
      {
        heading: '不是“盗版”的同义词',
        paragraphs: [
          '同人志首先是一种创作行为：作者自费印刷、自己排版、自己运送，把作品带到现场。它和商业出版的差别不在于质量，而在于决策权掌握在谁手里。',
          '很多职业漫画家正是从同人志开始被看见。这种低门槛的试错空间，是行业重要的蓄水池。',
        ],
      },
      {
        heading: '信任构成的流通',
        paragraphs: [
          '同人展的运转依赖大量非正式规则：排队方式、摊位间距、印刷数量的预估。这些规则没有强制力，却被参与者普遍遵守。',
          '它的效率惊人。一场大型展会可以在两天内完成数万本小规模出版物的流通，而组织成本远低于传统渠道。',
        ],
      },
      {
        heading: '共同体与边界',
        paragraphs: [
          '同人文化最珍贵的部分，是它允许“不成熟”。作者可以在几十位读者面前完成一次尝试，然后继续下一次。',
          '同时，它也需要清晰的边界意识：尊重原作、尊重他人、不把创作自由当成伤害的借口。这套共识，比任何规章都重要。',
        ],
      },
    ],
  },
  {
    id: 'chinese-animation-school',
    title: '中国动画学派的重生',
    subtitle: '水墨、留白与写意',
    summary:
      '《雾山五行》《中国奇谭》之后，越来越多创作者重新回到传统绘画语言里寻找方法论，而不只是找素材。',
    cover:
      'https://readdy.ai/api/search-image?query=Anime%20style%20editorial%20illustration%20of%20an%20ink%20wash%20landscape%20with%20mountains%2C%20mist%20and%20a%20tiny%20boat%20on%20a%20river%2C%20teal%20and%20gold%20tones%2C%20traditional%20Chinese%20scroll%20aesthetic%20blended%20with%20modern%20animation%2C%20painterly%20digital%20art%2C%20wide%20composition&width=1200&height=700&seq=culture-ink-landscape&orientation=landscape',
    author: '琉璃神社编辑部',
    date: '2026-08-30',
    readTime: '11 分钟',
    tags: ['国产动画', '水墨', '传统美学'],
    quote: '“留白不是没有东西，而是让观众把自己的经验放进去。”',
    content: [
      {
        heading: '被继承的不只是风格',
        paragraphs: [
          '上海美术电影制片厂时代的作品，把水墨、剪纸、木偶等民间技法引入动画，形成了被称作“中国动画学派”的独特语言。那批创作者做的不是视觉包装，而是把绘画的思维方式用到了时间艺术上。',
          '这种方法的重点是“取舍”。水墨不追求完整信息，而是用少量笔触提示结构，让观者自行补全。',
        ],
      },
      {
        heading: '新一代的实践',
        paragraphs: [
          '《雾山五行》把水墨的书写性带进了动作场面。打斗不再依赖密集的特效，而是靠笔势的方向与力度传达冲击，这在当前的动作动画里相当少见。',
          '《中国奇谭》则展示了另一种可能：多个短片、多种技法并存，把传统美术资源当成工具箱，而不是必须遵守的规范。',
        ],
      },
      {
        heading: '下一步的难题',
        paragraphs: [
          '真正的挑战在工业化。手绘水墨难以标准化，制作周期长，对人才要求高。要让它成为可持续的路径，需要的是流程与培养机制，而不是一两次爆发。',
          '但方向已经清晰：从“用中国元素”走向“用中国方法讲故事”。这一步跨过去，才谈得上学派的重生。',
        ],
      },
    ],
  },
];